import { useRef, useState } from 'react';
import { CopyIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui-kit/dialog';
import type { ApiKey } from './api-keys.types';
import { ApiKeyScopePicker } from './api-key-scope-picker';
import type { ApiKeyScopeGrant } from './api-key-scope-resolver';

export type KeyAction = { kind: 'issue' } | { kind: 'rotate' | 'revoke'; key: ApiKey };
export type SecretReveal = { appId: string; secret: string; rotated: boolean };

export function ApiKeyActionDialog({
  action,
  onClose,
  onConfirm,
  unavailable = false,
}: {
  action: KeyAction;
  onClose: () => void;
  onConfirm?: (
    name: string,
    grant: Pick<ApiKeyScopeGrant, 'roles' | 'permissions'>,
    itemId: string
  ) => Promise<void>;
  unavailable?: boolean;
}) {
  const [name, setName] = useState('');
  const itemId = action.kind === 'issue' ? '' : action.key.itemId;
  const [scopeGrant, setScopeGrant] = useState<ApiKeyScopeGrant>({
    explicitRoles: [],
    checkedRoles: [],
    impliedRoles: [],
    roles: [],
    permissions: [],
  });
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const issuing = action.kind === 'issue';
  const revoking = action.kind === 'revoke';
  const title = issuing
    ? 'Issue a new API key'
    : `${revoking ? 'Revoke' : 'Rotate'} key “${action.key.name}”?`;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submittingRef.current) onClose();
      }}
    >
      <DialogContent
        className="max-h-[90vh] max-w-xl overflow-y-auto bg-white"
        overlayClassName="bg-black/40"
        hideClose={isSubmitting}
      >
        <form
          className="flex flex-col gap-5"
          onSubmit={async (event) => {
            event.preventDefault();
            if (unavailable || !onConfirm || submittingRef.current) return;
            setSubmitError('');
            if (issuing && !name.trim()) {
              setError('Enter a name for this key.');
              return;
            }
            if (issuing && scopeGrant.roles.length === 0 && scopeGrant.permissions.length === 0) {
              setSubmitError('Select at least one scope.');
              return;
            }
            submittingRef.current = true;
            setIsSubmitting(true);
            try {
              await onConfirm(
                name.trim(),
                { roles: scopeGrant.roles, permissions: scopeGrant.permissions },
                itemId.trim()
              );
            } catch (error) {
              setSubmitError(
                error instanceof Error
                  ? error.message
                  : 'Could not complete the action. Please try again.'
              );
            } finally {
              submittingRef.current = false;
              setIsSubmitting(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {issuing
                ? 'Give your integration a recognizable name.'
                : revoking
                  ? 'Revocation is immediate and cannot be undone. Integrations using this key will stop working.'
                  : 'Submit this key’s existing itemId and settings to update its credentials. The key will not be deleted.'}
            </DialogDescription>
          </DialogHeader>
          {issuing && (
            <fieldset disabled={isSubmitting} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="api-key-name" className="text-sm font-medium">
                  Name
                </label>
                <Input
                  id="api-key-name"
                  autoFocus
                  maxLength={120}
                  placeholder="e.g. Website signup form"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError('');
                  }}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'api-key-name-error' : undefined}
                />
                {error && (
                  <p id="api-key-name-error" role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}
              </div>
              <ApiKeyScopePicker selection={scopeGrant} onChange={setScopeGrant} />
            </fieldset>
          )}
          {(unavailable || issuing) && (
            <p className="text-xs text-muted-foreground">
              {unavailable
                ? 'This action is not available yet. No keys will be changed.'
                : 'Access tokens issued with this key are valid for 15 minutes.'}
            </p>
          )}
          {submitError && (
            <p role="alert" className="text-sm text-destructive">
              {submitError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={isSubmitting} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={unavailable || isSubmitting}
              loading={isSubmitting}
              variant={revoking ? 'destructive' : 'default'}
            >
              {issuing ? 'Issue key' : revoking ? 'Revoke key' : 'Rotate key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ApiKeySecretDialog({
  reveal,
  onClose,
}: {
  reveal: SecretReveal;
  onClose: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const [message, setMessage] = useState('');
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage('Copied.');
    } catch {
      setMessage('Could not copy. Select and copy the value manually.');
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && saved) onClose();
      }}
    >
      <DialogContent
        hideClose
        className="max-w-xl bg-white"
        overlayClassName="bg-black/40"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {reveal.rotated ? 'Key rotated' : 'Key issued'} — copy the secret
          </DialogTitle>
          <DialogDescription>
            This is the only time the secret is shown. Copy it before closing.
          </DialogDescription>
        </DialogHeader>
        <p className="rounded-md bg-warning/10 p-3 text-sm">
          Demo credentials only — these cannot authenticate API requests.
        </p>
        {[
          { label: 'App ID', value: reveal.appId },
          { label: 'Client secret', value: reveal.secret },
        ].map(({ label, value }) => (
          <div key={label} className="space-y-2">
            <label className="text-sm font-medium" htmlFor={`reveal-${label.replace(/ /g, '-')}`}>
              {label}
            </label>
            <div className="flex gap-2">
              <Input
                id={`reveal-${label.replace(/ /g, '-')}`}
                readOnly
                value={value}
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                size="icon"
                aria-label={`Copy ${label.toLowerCase()}`}
                onClick={() => void copy(value)}
              >
                <CopyIcon size={16} />
              </Button>
            </div>
          </div>
        ))}
        <p role="status" className="text-sm text-muted-foreground">
          {message}
        </p>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1 accent-primary"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
          />
          I’ve copied this secret and stored it securely
        </label>
        <DialogFooter>
          <Button disabled={!saved} onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
